import type {
  DeviceInfo,
  DeviceShadowApiSchema,
  DeviceShadowData as DeviceState,
  DeviceShadowHardwareMacAddresses,
  DeviceShadowHealthData,
  DeviceShadowSettingsDailyRestart,
  NativePlayer,
} from "@screencloud/native-players-helpers";
import type { HorizonColdMetadata } from "@screencloud/native-players-helpers/device-state";
import type { DeviceDetails } from "@screencloud/rdm-api-client";
import { DevicePlatform } from "../../constants/player";

/** Keys of `T` whose value is a boolean flag (excludes numeric capabilities). */
type BooleanKeys<T> = {
  [K in keyof T]-?: NonNullable<T[K]> extends boolean ? K : never;
}[keyof T];

const API_SCHEMA_CAPABILITY_MAP: Record<
  string,
  {
    cap?: BooleanKeys<NonNullable<NativePlayer["capabilities"]>>;
    cmd?: BooleanKeys<NonNullable<NativePlayer["screen_api_command"]>>;
  }
> = {
  "device.reboot": { cap: "reboot", cmd: "reboot" },
  "device.clear_cache": { cap: "clear_cache", cmd: "clear_cache" },
  "app.restart": { cap: "restart", cmd: "restart" },
  "device.restart_player_app": { cap: "restart", cmd: "restart" },
  "device.set_mute": { cap: "mute", cmd: "mute" },
  "app.refresh": { cap: "refresh", cmd: "refresh" },
  "screen.capture": { cap: "screenshot" },
  "os.channel": { cap: "change_update_channel", cmd: "change_update_channel" },
};

export function parseUpdatedAtMs(iso: unknown): number | undefined {
  if (typeof iso !== "string" || !iso) return undefined;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? undefined : ms;
}

export function pickString(...values: unknown[]): string | undefined {
  for (const v of values) {
    if (typeof v === "string" && v.length > 0) return v;
  }
  return undefined;
}

/** Horizon metadata stores the player id under `device_identifiers`, not `hardware`. */
function hardwareIdFromDeviceIdentifiers(
  metadata: DeviceState["metadata"] | null | undefined,
): string | undefined {
  if (
    metadata == null ||
    typeof metadata !== "object" ||
    !("device_identifiers" in metadata)
  ) {
    return undefined;
  }

  const identifiers = (metadata as HorizonColdMetadata).device_identifiers;
  return pickString(identifiers?.hardware_id);
}

/**
 * Picks the MAC address for the active connection: the ethernet MAC when the
 * device is on ethernet, otherwise the wifi MAC. Returns undefined when the
 * matching MAC is missing — does not fall back to the other interface.
 */
export function pickMacAddress(
  metaMac: DeviceShadowHardwareMacAddresses | undefined,
  connectionType: string | undefined,
): string | undefined {
  return connectionType === "ethernet"
    ? pickString(metaMac?.ethernet)
    : pickString(metaMac?.wifi);
}

/**
 * Legacy `device.temperatures` is the CPU °C as a numeric string; the UI
 * `parseInt`s it, so only the CPU value is returned.
 */
export function formatTemperatures(
  temperatures: DeviceShadowHealthData["temperatures"] | string | undefined,
): string | undefined {
  if (typeof temperatures === "string") return temperatures;
  const cpu = temperatures?.cpu;
  return cpu != null ? String(cpu) : undefined;
}

export function formatDailyRestartFromState(
  dailyRestart: DeviceShadowSettingsDailyRestart | undefined,
): {
  daily_restart?: string;
  daily_restart_time?: string;
} {
  if (!dailyRestart) return {};

  const enabled = dailyRestart.enabled === true;
  const hour = pickString(dailyRestart.hour);
  const minute = pickString(dailyRestart.minute) ?? "00";
  const time =
    hour != null
      ? `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`
      : undefined;

  return {
    daily_restart: enabled ? "on" : "off",
    ...(time ? { daily_restart_time: time } : {}),
  };
}

export function capabilitiesFromApiSchema(
  apiSchema: DeviceShadowApiSchema | null | undefined,
): Pick<NativePlayer, "capabilities" | "screen_api_command"> {
  if (!apiSchema || typeof apiSchema !== "object" || Array.isArray(apiSchema)) {
    return { capabilities: {}, screen_api_command: {} };
  }

  const capabilities: NonNullable<NativePlayer["capabilities"]> = {};
  const screen_api_command: NonNullable<NativePlayer["screen_api_command"]> =
    {};

  for (const commandType of Object.keys(apiSchema)) {
    const mapping = API_SCHEMA_CAPABILITY_MAP[commandType];
    if (!mapping) continue;
    if (mapping.cap) capabilities[mapping.cap] = true;
    if (mapping.cmd) screen_api_command[mapping.cmd] = true;
  }

  return { capabilities, screen_api_command };
}

export function getMuteFromDeviceState(
  deviceState: DeviceState | undefined,
): boolean | undefined {
  return deviceState?.state?.settings?.mute;
}

/**
 * Maps Device API device state into legacy `DeviceInfo` for existing UI.
 */
export function deviceStateAdapterToDeviceInfo(
  deviceState: DeviceState,
): Partial<DeviceInfo> | null {
  if (!deviceState.isOnline || !deviceState.state) return null;

  const state = deviceState.state;
  const network = state.network;
  const health = state.health;
  const memory = state.memory;
  const storage = state.storage;
  const display = state.hardware?.display;
  const settings = state.settings;
  const signage = state.signage;

  // os, hardware (platform/model/architect/mac), and timezone live under
  // `metadata`, NOT `state`. `state` only carries live telemetry (network,
  // health, memory, storage, display dims, settings, signage, hardware_id).
  const metadata = deviceState.metadata;
  const metaOs = metadata?.os;
  const metaHardware = metadata?.hardware;
  const metaScreen = metaHardware?.screen;
  const metaTime = metadata?.time;
  const metaMac = metaHardware?.mac_addresses;

  const updated_at =
    parseUpdatedAtMs(deviceState.updatedAt) ??
    parseUpdatedAtMs(state.last_seen);

  const dailyRestartSettings = formatDailyRestartFromState(
    settings?.daily_restart,
  );

  const { capabilities, screen_api_command } = capabilitiesFromApiSchema(
    deviceState.apiSchema,
  );

  // OS version is reported split as version_name + version_code; legacy UI
  // expects the combined "name (code)" string, e.g. "1.3.4-14-g5e0749d (0)".
  const osVersionName = pickString(metaOs?.version_name);
  const osVersionCode = pickString(metaOs?.version_code);
  const osVersion = osVersionName
    ? osVersionCode
      ? `${osVersionName} (${osVersionCode})`
      : osVersionName
    : undefined;

  const platform = pickString(
    metaHardware?.platform,
    metaOs?.name,
  )?.toLowerCase();
  const orientation = pickString(metaScreen?.native_orientation);
  const native_player: NativePlayer = {
    // Player id sent to rdm-api. Prefer the Device API column, then Horizon
    // metadata, then the two legacy hardware_id locations. deviceId is last.
    id: pickString(
      deviceState.hardwareId,
      hardwareIdFromDeviceIdentifiers(metadata),
      state.hardware_id,
      metaHardware?.hardware_id,
      deviceState.deviceId,
    ),
    // The SC player-app version is not reported by the Device API yet; leave
    // undefined rather than mislabel the OS version as the player version.
    version: undefined,
    platform,
    timezone: pickString(metaTime?.timezone),
    settings: {
      mute: settings?.mute,
      ...dailyRestartSettings,
      media_cache_port: settings?.media_cache_port,
      local_proxy_port: settings?.local_proxy_port,
      orientation,
    },
    capabilities,
    screen_api_command,
    custom_data: undefined,
  };

  const scaleFactor = 1; // TODO: PIXI device state reported calculated resolution, not rendered resolution.
  // We don't need to compute with pixel ratio. Here we set it to 1 for now.
  // metaScreen?.scale_factor;

  const device: NonNullable<DeviceInfo["device"]> = {
    ip: pickString(network?.ip_address),
    // PIXI reports network hostname as 'Unknown'; use settings.display_name instead
    hostname:
      platform === DevicePlatform.PIXI
        ? pickString(settings?.display_name)
        : pickString(network?.hostname),
    mac_address: pickMacAddress(metaMac, network?.connection_type),
    model: pickString(metaHardware?.model, metaHardware?.device_model),
    available_memory: pickString(memory?.available, health?.available_memory),
    total_memory: pickString(memory?.total, health?.total_memory),
    available_storage: pickString(
      storage?.available,
      health?.available_storage,
    ),
    total_storage: pickString(storage?.total, health?.total_storage),
    temperatures: formatTemperatures(health?.temperatures),
    display: {
      width: display?.width,
      height: display?.height,
      scaleFactor,
      orientation,
    },
    os: {
      version: osVersion,
      type: pickString(metaOs?.name),
      model: pickString(metaOs?.model, metaHardware?.model),
      architect: pickString(metaHardware?.architect),
    },
  };

  const web_player: DeviceInfo["web_player"] = signage
    ? {
        content_player: {
          screen_id: pickString(signage.screen_id),
          device_id: pickString(signage.device_id),
          org_id: pickString(signage.org_id),
          space_id: pickString(signage.space_id),
        },
      }
    : undefined;

  const partial: Partial<DeviceInfo> = {
    device,
    native_player,
    web_player,
    ...(updated_at != null ? { updated_at } : {}),
  };

  return partial;
}

/**
 * Adapts `deviceState.state.network` (new Device API shape) to the
 * `DeviceDetails["shadow"]["network"]` shape consumed by the Network component.
 * Reads state directly — independent of `isOnline` so last-known data shows
 * when the device is offline.
 */
export function deviceStateToSCOSNetworkInfo(
  deviceState: DeviceState,
): NonNullable<DeviceDetails["shadow"]["network"]> {
  const network = deviceState.state?.network;
  const metaMac = deviceState.metadata?.hardware?.mac_addresses;

  const rssi = network?.wifi_rssi;
  // Convert dBm to a 0–100 quality percentage: 2*(rssi+100), clamped.
  // e.g. −49 dBm → 102 → 100 (Excellent); −80 dBm → 40 (Low)
  const signal =
    typeof rssi === "number"
      ? String(Math.min(100, Math.max(0, 2 * (rssi + 100))))
      : "";

  const ip = pickString(network?.ip_address);
  // `mode` is a required "wifi" | "ethernet"; default to "wifi" when the
  // device doesn't report a recognised connection type.
  const mode =
    pickString(network?.connection_type) === "ethernet" ? "ethernet" : "wifi";
  const ntpServer = pickString(deviceState.metadata?.time?.ntp_server);

  return {
    ssid: pickString(network?.wifi_ssid) ?? "",
    mode,
    signal,
    ip_address: ip ? [ip] : [],
    security: pickString(network?.wifi_security) ?? "",
    mac_address: pickMacAddress(metaMac, mode) ?? "",
    dns: network?.dns ?? [],
    bssid: "",
    speed: "",
    duplex: "",
    routes: { default_route: pickString(network?.gateway) ?? "" },
    client_certificate: "",
    ca_certificate: "",
    domains: [],
    ntp: ntpServer ? [ntpServer] : [],
    proxy: "",
    device: "",
  };
}
